import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Button, Tag, Space, message, Modal, Descriptions
} from 'antd';
import {
  EditOutlined, DeleteOutlined, EyeOutlined,
  RocketOutlined
} from '@ant-design/icons';
import api from '../utils/api';
import moment from 'moment';

const AdCreationDrafts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);

  // 获取草稿列表（包含所有状态）
  const fetchDrafts = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      // 获取所有状态的任务，支持分页
      const response = await api.get('/ad-creation-tasks/', {
        params: { page, page_size: pageSize }
      });
      const data = response.data || {};
      setDrafts(data.tasks || []);
      setPagination({
        current: data.page || 1,
        pageSize: data.page_size || 10,
        total: data.total || 0
      });
    } catch (error) {
      message.error('获取草稿列表失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  // 删除草稿
  const handleDelete = async (taskId) => {
    Modal.confirm({
      title: '确认删除',
      content: '删除后无法恢复，是否继续？',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await api.delete(`/ad-creation-tasks/${taskId}`);
          message.success('删除成功');
          fetchDrafts();
        } catch (error) {
          message.error('删除失败: ' + (error.response?.data?.detail || error.message));
        }
      },
    });
  };

  // 继续编辑
  const handleContinue = (draft) => {
    // 跳转到创建广告页面，带上 task_id 参数
    navigate('/ad-creation', { state: { draftTaskId: draft.task_id, currentStep: draft.current_step || 1 } });
  };

  // 查看详情
  const handleViewDetail = (draft) => {
    setSelectedDraft(draft);
    setDetailModalVisible(true);
  };

  // 判断是否所有广告都已完成 - 以 ad_id 是否存在为判断
  const isAllAdsDone = (record) => {
    if (!record.ad_total || record.ad_total === 0) return false;
    // 广告成功数以 ad_id 是否存在为判断，这个逻辑由后端统计返回
    return record.ad_success === record.ad_total;
  };

  // 步骤标签 - 只有所有广告都完成才显示"已完成"
  const getStepTag = (step, status, record) => {
    if (isAllAdsDone(record)) {
      return <Tag color="success">已完成</Tag>;
    }
    const stepMap = {
      1: { color: 'blue', text: 'Campaign' },
      2: { color: 'orange', text: 'AdSet' },
      3: { color: 'purple', text: 'Ad' },
      4: { color: 'purple', text: 'Ad' },
    };
    const s = stepMap[step] || { color: 'default', text: step ? `步骤${step}` : '-' };
    return <Tag color={s.color}>{s.text}</Tag>;
  };

  // 状态列渲染 - 三行显示
  const renderStatusColumn = (record) => {
    return (
      <div style={{ lineHeight: '1.8' }}>
        <div>
          系列：{record.campaign_total || 1}/{record.campaign_success || 0}/
          <span style={{ color: '#ff4d4f' }}>{record.campaign_failed || 0}</span>
        </div>
        <div>
          组：{record.adset_total || 0}/{record.adset_success || 0}/
          <span style={{ color: '#ff4d4f' }}>{record.adset_failed || 0}</span>
        </div>
        <div>
          广告：{record.ad_total || 0}/{record.ad_success || 0}/
          <span style={{ color: '#ff4d4f' }}>{record.ad_failed || 0}</span>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: '任务ID',
      dataIndex: 'task_id',
      key: 'task_id',
      width: 220,
      ellipsis: true,
    },
    {
      title: '广告账户',
      dataIndex: 'ad_account_name',
      key: 'ad_account_name',
      render: (text) => text || '-',
    },
    {
      title: '基础名称',
      dataIndex: 'base_name',
      key: 'base_name',
    },
    {
      title: 'Campaign',
      key: 'campaign',
      ellipsis: true,
      render: (_, record) => (
        <div style={{ lineHeight: 1.6 }}>
          <div>{record.campaign_name || record.campaign_display_name || '-'}</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
            {record.campaign_id || '-'}
          </div>
        </div>
      ),
    },
    {
      title: '预算模式',
      dataIndex: 'budget_mode',
      key: 'budget_mode',
      width: 100,
      render: (text) => <Tag>{text || 'CBO'}</Tag>,
    },
    {
      title: '当前步骤',
      dataIndex: 'current_step',
      key: 'current_step',
      width: 120,
      render: (step, record) => getStepTag(step, record.status, record),
    },
    {
      title: '状态',
      key: 'status',
      width: 180,
      render: (_, record) => renderStatusColumn(record),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleContinue(record)}
          >
            继续
          </Button>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.task_id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <RocketOutlined />
            <span>广告创建草稿</span>
          </Space>
        }
        extra={
          <Button type="primary" onClick={() => navigate('/ad-creation')}>
            新建广告
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={drafts}
          rowKey="task_id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: (page, pageSize) => fetchDrafts(page, pageSize)
          }}
        />
      </Card>

      {/* 详情弹窗 */}
      <Modal
        title="草稿详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>,
          <Button
            key="continue"
            type="primary"
            onClick={() => {
              setDetailModalVisible(false);
              handleContinue(selectedDraft);
            }}
          >
            继续编辑
          </Button>,
        ]}
        width={600}
      >
        {selectedDraft && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="任务ID">{selectedDraft.task_id}</Descriptions.Item>
            <Descriptions.Item label="基础名称">{selectedDraft.base_name}</Descriptions.Item>
            <Descriptions.Item label="Campaign">
              <div>{selectedDraft.campaign_name || selectedDraft.campaign_display_name || '-'}</div>
              <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 12 }}>
                {selectedDraft.campaign_id || '-'}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="广告账户">{selectedDraft.ad_account_name || '-'}</Descriptions.Item>
            <Descriptions.Item label="预算模式">{selectedDraft.budget_mode || 'CBO'}</Descriptions.Item>
            <Descriptions.Item label="当前步骤">{getStepTag(selectedDraft.current_step, selectedDraft.status, selectedDraft)}</Descriptions.Item>
            <Descriptions.Item label="状态">
              {renderStatusColumn(selectedDraft)}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {selectedDraft.created_at ? moment(selectedDraft.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {selectedDraft.updated_at ? moment(selectedDraft.updated_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Campaign参数">
              <pre style={{ maxHeight: 200, overflow: 'auto' }}>
                {JSON.stringify(selectedDraft.campaign_params || {}, null, 2)}
              </pre>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default AdCreationDrafts;